import React, { useState, useEffect, useRef, useCallback } from "react";
import { PersonalChat } from "./PersonalChat";
import { GroupChat } from "./GroupChat";
import { useUnreadCount } from "./useUnreadCount";
import { useFaviconBadge } from "../../hooks/useFaviconBadge";
import { useGetAgentStatusQuery, useGetPresenceQuery } from "../../services/rtkApi/agentApi";
import { Tabs, TabsList, TabsTrigger, TabsContent, useAnyMobileSheetOpen } from "../../ui";
import AgentChatSheet from "./AgentChatSheet";
import "./agent_chat_widget.css";

const statusColors: Record<string, string> = {
  in_chat: "#22c55e",
  online: "#22c55e",
  idle: "#eab308",
  busy: "#eab308",
  away: "#a855f7",
  offline: "#ef4444",
  out_of_tokens: "#ea580c",
};

const statusFallbacks: Record<string, string> = {
  in_chat: "in chat",
  online: "online",
  idle: "idle",
  busy: "idle",
  away: "away",
  offline: "offline",
  out_of_tokens: "out of credit",
};

export default function AgentChatWidget() {
  // desktop panel
  const [isOpen, setIsOpen] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  // mobile sheet
  const [sheetOpen, setSheetOpen] = useState(false);
  // shared between both — one room selection, wherever the chat is rendered
  const [activeTab, setActiveTab] = useState("group");
  const panelRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // SSE is the primary update path — polling is just a fallback
  const { data: agentStatus } = useGetAgentStatusQuery(undefined, {
    pollingInterval: 30_000,
  });
  const rawStatus = agentStatus?.status || "offline";
  // treat busy+presence as "in_chat" for the widget dot
  const isInChat = rawStatus === "in_chat" ||
    (rawStatus === "busy" && !!agentStatus?.presence);
  const status = isInChat ? "in_chat" : rawStatus;
  const dotColor = statusColors[status] || statusColors.offline;
  const statusLabel = isInChat
    ? (agentStatus?.current_activity || "in chat")
    : rawStatus === "out_of_tokens"
      ? (agentStatus?.current_activity || statusFallbacks.out_of_tokens || "offline")
      : (statusFallbacks[status] || "offline");
  const shouldPulse = isInChat;

  // SSE handles live updates; polling is a 60s fallback
  const { data: presenceData } = useGetPresenceQuery(undefined, {
    pollingInterval: 60_000,
  });
  const onlineCount = presenceData?.count ?? presenceData?.users?.length ?? 0;

  const { personalUnread, groupUnread, totalUnread, markPersonalRead, markGroupRead } =
    useUnreadCount();

  useFaviconBadge(totalUnread > 0);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 700);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const chatShowing = isMobile ? sheetOpen : isOpen;

  // mark read when switching tabs
  useEffect(() => {
    if (!chatShowing) return;
    if (activeTab === "personal") markPersonalRead();
    if (activeTab === "group") markGroupRead();
  }, [activeTab, chatShowing, markPersonalRead, markGroupRead]);

  // close on outside click (desktop only)
  useEffect(() => {
    if (isMobile || !isOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, isMobile]);

  // show panel in DOM when opening, hide it after close animation finishes
  useEffect(() => {
    if (isOpen) setPanelVisible(true);
  }, [isOpen]);

  const handlePanelAnimationEnd = useCallback(() => {
    if (!isOpen) setPanelVisible(false);
  }, [isOpen]);

  const openChat = useCallback((room?: "personal" | "group") => {
    if (room) setActiveTab(room);
    const target = room ?? (activeTab as "personal" | "group");
    if (window.innerWidth <= 700) setSheetOpen(true);
    else setIsOpen(true);
    if (target === "personal") markPersonalRead();
    if (target === "group") markGroupRead();
  }, [activeTab, markPersonalRead, markGroupRead]);

  const toggle = useCallback(() => {
    if (isMobile) {
      setSheetOpen((prev) => !prev);
    } else {
      setIsOpen((prev) => !prev);
    }
  }, [isMobile]);

  const closeSheet = useCallback(() => setSheetOpen(false), []);

  // listen for programmatic open events (from mobile nav chat button)
  useEffect(() => {
    const handler = () => openChat("group");
    window.addEventListener("n450s:open-agent-chat", handler);
    return () => window.removeEventListener("n450s:open-agent-chat", handler);
  }, [openChat]);

  // the siteline dock shares the bottom-right column on desktop — one at a time
  useEffect(() => {
    const handler = (e: Event) => {
      if ((e as CustomEvent).detail?.open) setIsOpen(false);
    };
    window.addEventListener("n450s:siteline-dock-change", handler);
    return () => window.removeEventListener("n450s:siteline-dock-change", handler);
  }, []);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const panelClassName = !panelVisible
    ? "agent-widget-panel agent-widget-panel--hidden"
    : isOpen
      ? "agent-widget-panel agent-widget-panel--open"
      : "agent-widget-panel agent-widget-panel--closed";

  // check if we should use legacy layout (skip rendering on legacy mobile)
  const [useLegacy, setUseLegacy] = useState(() => {
    try {
      return localStorage.getItem("useLegacyLayout") === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handler = () => {
      try {
        setUseLegacy(localStorage.getItem("useLegacyLayout") === "true");
      } catch {
        // ignore
      }
    };
    window.addEventListener("storage", handler);
    window.addEventListener("legacyLayoutChanged", handler);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener("legacyLayoutChanged", handler);
    };
  }, []);

  // close mobile sheet when switching to desktop (and vice versa)
  useEffect(() => {
    if (!isMobile) setSheetOpen(false);
    else setIsOpen(false);
  }, [isMobile]);

  // notify other components (e.g. MobileNavBar) when the chat sheet opens/closes
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("n450s:chat-sheet-change", { detail: { open: sheetOpen } }),
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent("n450s:chat-sheet-change", { detail: { open: false } }),
      );
    };
  }, [sheetOpen]);

  // the desktop twin of the event above — the siteline dock stashes its chip
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("n450s:chat-panel-change", { detail: { open: isOpen } }),
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent("n450s:chat-panel-change", { detail: { open: false } }),
      );
    };
  }, [isOpen]);

  // the mobile chip floats above the menu sheet (MobileNavBar); step aside
  // while the menu is open
  const [navSheetOpen, setNavSheetOpen] = useState(false);
  useEffect(() => {
    const handler = (e: Event) => setNavSheetOpen(!!(e as CustomEvent).detail?.open);
    window.addEventListener("n450s:nav-sheet-change", handler);
    return () => window.removeEventListener("n450s:nav-sheet-change", handler);
  }, []);
  // and for any other bottom sheet: the chip stacks above a non-modal sheet
  // and covered its footer actions (the add-coffee Next button)
  const bottomSheetOpen = useAnyMobileSheetOpen();

  const groupHistorySubscriptionActive = chatShowing && activeTab === "group";
  // whether the personal chat is actually on screen — gates its presence heartbeat
  const personalChatActive = chatShowing && activeTab === "personal";

  const hideMobileWidget = isMobile && useLegacy;

  // room switcher + the status line that used to float over the page as two
  // separate tiles. It belongs to the chat, so it lives inside it.
  const rooms = (
    <div className="agent-chat-rooms">
      <TabsList className="w-full">
        <TabsTrigger value="group" className="flex-1 gap-1">
          general
          {groupUnread > 0 && (
            <span className="agent-widget-tab-badge">{groupUnread}</span>
          )}
        </TabsTrigger>
        <TabsTrigger value="personal" className="flex-1 gap-1">
          personal
          {personalUnread > 0 && (
            <span className="agent-widget-tab-badge">{personalUnread}</span>
          )}
        </TabsTrigger>
      </TabsList>
      <div className="agent-chat-rooms__status">
        <span
          className={`agent-chat-rooms__dot${shouldPulse ? " agent-chat-rooms__dot--pulse" : ""}`}
          style={{ backgroundColor: dotColor, color: dotColor }}
        />
        <span className="agent-chat-rooms__status-text">sitebot {statusLabel}</span>
        <span className="agent-chat-rooms__sep">·</span>
        <span className="agent-chat-rooms__status-text">
          {onlineCount > 0
            ? `${onlineCount > 99 ? "99+" : onlineCount} online`
            : "general empty"}
        </span>
      </div>
    </div>
  );

  const chatBody = (
    <>
      <TabsContent value="group" className="flex-1 overflow-hidden mt-0">
        <GroupChat historySubscriptionActive={groupHistorySubscriptionActive} />
      </TabsContent>
      <TabsContent value="personal" className="flex-1 overflow-hidden mt-0">
        <PersonalChat active={personalChatActive} />
      </TabsContent>
    </>
  );

  return (
    <>
      {/* ── desktop: bottom-right chip, panel grows upward from it ── */}
      {!isMobile && (
        <div className="agent-widget-desktop" ref={panelRef}>
          {/* panel */}
          <div className={panelClassName} onAnimationEnd={handlePanelAnimationEnd}>
            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="agent-chat-panel-inner flex flex-col h-full min-h-0"
            >
              {rooms}
              {chatBody}
            </Tabs>
          </div>

          {/* toggle chip */}
          <button
            className={"agent-widget-chip" + (isOpen ? " is-open" : "")}
            onClick={toggle}
            aria-expanded={isOpen}
            aria-label={isOpen ? "close the sitebot chat" : "open the sitebot chat"}
          >
            <span
              className={`agent-widget-chip__dot${shouldPulse ? " agent-widget-chip__dot--pulse" : ""}`}
              style={{ backgroundColor: dotColor, color: dotColor }}
            />
            <span className="agent-widget-chip__label">sitebot</span>
            {totalUnread > 0 && (
              <span className="agent-widget-badge">{totalUnread > 9 ? "9+" : totalUnread}</span>
            )}
          </button>
        </div>
      )}

      {/* ── mobile: one compact chip + a viewport-sized sheet ── */}
      {isMobile && !hideMobileWidget && (
        <>
          {!sheetOpen && !navSheetOpen && !bottomSheetOpen && (
            <button
              className="agent-widget-chip agent-widget-chip--mobile"
              onClick={() => openChat()}
              aria-label="open the sitebot chat"
            >
              <span
                className={`agent-widget-chip__dot${shouldPulse ? " agent-widget-chip__dot--pulse" : ""}`}
                style={{ backgroundColor: dotColor, color: dotColor }}
              />
              <span className="agent-widget-chip__label">sitebot</span>
              {totalUnread > 0 && (
                <span className="agent-widget-badge">{totalUnread > 9 ? "9+" : totalUnread}</span>
              )}
            </button>
          )}

          <AgentChatSheet open={sheetOpen} onClose={closeSheet}>
            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="flex flex-col h-full min-h-0"
            >
              {rooms}
              {chatBody}
            </Tabs>
          </AgentChatSheet>
        </>
      )}
    </>
  );
}
